import React, { useState } from "react";
import { useNotify, useRefresh } from "react-admin";
import DownloadIcon from "@mui/icons-material/Download";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import {
  CsvImportDialog,
  CsvImportTriggerButton,
  useCanCsvImport,
  useCsvImportDialogState,
} from "@/components/csv-import";
import { getApiUrl } from "@/constants";
import httpClient from "@/dataprovider/http-client";
import { formatListDate } from "@/utils/date-format";
import {
  countOfficeHolidayPreviewIssues,
  downloadOfficeHolidaysCsvTemplate,
  officeHolidayPreviewCanImport,
  officeHolidayPreviewHasErrors,
  officeHolidayRowsToImportPayload,
  parseOfficeHolidaysImportFile,
  type OfficeHolidayPreviewRow,
  type ParseOfficeHolidaysImportResult,
} from "./parse-office-holidays-import";

type ImportResult = {
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
  errors: { rowNumber: number; message: string }[];
};

const FILE_ACCEPT =
  ".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

function formatPreviewDate(row: OfficeHolidayPreviewRow): string {
  if (row.date) {
    return formatListDate(row.date, { empty: row.dateText || "—" });
  }
  return row.dateText || "—";
}

export function OfficeHolidaysImportButton() {
  const notify = useNotify();
  const refresh = useRefresh();
  const canImport = useCanCsvImport([
    "officeHolidays.create",
    "officeHolidays.edit",
    "employees.edit",
  ]);
  const { open, busy, setBusy, inputRef, openDialog, closeDialog, chooseFile } =
    useCsvImportDialogState();
  const [fileLabel, setFileLabel] = useState("");
  const [preview, setPreview] = useState<ParseOfficeHolidaysImportResult | null>(null);
  const [parseFatal, setParseFatal] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<ImportResult | null>(null);

  if (!canImport) {
    return null;
  }

  const resetPreviewState = () => {
    setPreview(null);
    setParseFatal(null);
    setFileLabel("");
    setLastResult(null);
  };

  const handleClose = () => {
    if (busy) return;
    closeDialog();
    resetPreviewState();
  };

  const onFileSelected = async (file: File) => {
    setBusy(true);
    setLastResult(null);
    setPreview(null);
    setParseFatal(null);
    setFileLabel(file.name);
    try {
      const parsed = await parseOfficeHolidaysImportFile(file);
      setPreview(parsed);
      const issueCount = countOfficeHolidayPreviewIssues(parsed.previewRows);
      if (parsed.rows.length === 0) {
        notify(
          issueCount > 0
            ? `No valid rows to import (${issueCount} issue(s)). Fix the file and choose again.`
            : "No valid rows to import.",
          { type: "warning" }
        );
      } else if (issueCount > 0) {
        notify(
          `Preview ready: ${parsed.rows.length} row(s) will import; ${issueCount} row(s) have issues.`,
          { type: "warning" }
        );
      } else {
        notify(`Preview ready: ${parsed.rows.length} holiday(s). Confirm to import.`, {
          type: "info",
        });
      }
    } catch (err) {
      setParseFatal(err instanceof Error ? err.message : "Invalid file");
      notify(err instanceof Error ? err.message : "Invalid file", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    if (!preview || !officeHolidayPreviewCanImport(preview)) return;

    setBusy(true);
    setLastResult(null);
    try {
      const rowsPayload = officeHolidayRowsToImportPayload(preview.rows);
      const headers = new Headers();
      headers.set("Content-Type", "application/json");

      const { json } = await httpClient(`${getApiUrl()}/officeHolidays/bulk-import`, {
        method: "POST",
        headers,
        body: JSON.stringify({ rows: rowsPayload }),
      });

      const body = json as Partial<ImportResult> & {
        message?: string | string[];
        errors?: { rowNumber: number; message: string }[];
      };

      const serverErrors = Array.isArray(body.errors) ? body.errors : [];
      const result: ImportResult = {
        created: body.created ?? 0,
        updated: body.updated ?? 0,
        unchanged: body.unchanged ?? 0,
        skipped: preview.skippedBlankRows,
        errors: serverErrors,
      };
      setLastResult(result);

      const touched = result.created + result.updated;
      if (touched > 0) {
        notify(`Imported ${result.created} new, updated ${result.updated} holiday(s).`, {
          type: "success",
        });
      } else if (serverErrors.length > 0) {
        notify(`No changes applied. ${serverErrors.length} row issue(s).`, { type: "warning" });
      } else {
        notify("No changes applied (holidays already up to date).", { type: "info" });
      }
      refresh();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Import failed", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const canConfirm = Boolean(preview && officeHolidayPreviewCanImport(preview) && !busy);
  const issueCount = preview ? countOfficeHolidayPreviewIssues(preview.previewRows) : 0;

  return (
    <>
      <Button
        startIcon={<DownloadIcon />}
        onClick={downloadOfficeHolidaysCsvTemplate}
        size="small"
        variant="outlined"
      >
        Download template
      </Button>
      <CsvImportTriggerButton
        label="Import CSV / Excel"
        onClick={() => {
          resetPreviewState();
          openDialog();
        }}
      />

      <CsvImportDialog
        open={open}
        onClose={handleClose}
        busy={busy}
        maxWidth="md"
        title="Import office holidays"
        progress={busy || undefined}
        fileInputRef={inputRef}
        accept={FILE_ACCEPT}
        onFileSelected={onFileSelected}
        actions={
          <>
            <Button onClick={handleClose} disabled={busy}>
              Close
            </Button>
            <Button variant="outlined" disabled={busy} onClick={downloadOfficeHolidaysCsvTemplate}>
              Download template
            </Button>
            <Button variant="outlined" disabled={busy} onClick={chooseFile}>
              Choose CSV / Excel file
            </Button>
            <Button variant="contained" disabled={!canConfirm} onClick={runImport}>
              {busy && preview ? "Importing…" : "Import"}
            </Button>
          </>
        }
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          Choose a CSV or Excel file to preview. Review highlighted issues, then confirm Import.
          Existing holidays on the same date are updated.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontFamily: "monospace" }}>
          date,name,notes
        </Typography>
        {fileLabel ? (
          <Typography variant="body2" sx={{ mb: 1 }}>
            File: <strong>{fileLabel}</strong>
          </Typography>
        ) : null}

        {parseFatal ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {parseFatal}
          </Alert>
        ) : null}

        {preview ? (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" gutterBottom>
              Preview: <strong>{preview.previewRows.length}</strong> row
              {preview.previewRows.length === 1 ? "" : "s"}
              {preview.skippedBlankRows > 0
                ? ` (${preview.skippedBlankRows} blank skipped)`
                : ""}. <strong>{preview.rows.length}</strong> will import
              {issueCount > 0 ? (
                <>
                  ; <strong>{issueCount}</strong> with issues (won’t import)
                </>
              ) : null}
              .
            </Typography>
            <TableContainer sx={{ maxHeight: 360, mt: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.previewRows.map((row) => {
                    const hasErrors = officeHolidayPreviewHasErrors(row);
                    return (
                      <TableRow
                        key={row.rowNumber}
                        sx={(theme) =>
                          hasErrors
                            ? {
                                bgcolor: alpha(
                                  theme.palette.error.main,
                                  theme.palette.mode === "dark" ? 0.22 : 0.12
                                ),
                              }
                            : {}
                        }
                      >
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell
                          sx={(theme) =>
                            !row.date ? { color: theme.palette.error.main, fontWeight: 600 } : {}
                          }
                        >
                          {formatPreviewDate(row)}
                        </TableCell>
                        <TableCell
                          sx={(theme) =>
                            !row.name.trim()
                              ? { color: theme.palette.error.main, fontWeight: 600 }
                              : {}
                          }
                        >
                          {row.name || "—"}
                        </TableCell>
                        <TableCell>{row.notes?.trim() || "—"}</TableCell>
                        <TableCell
                          sx={(theme) =>
                            hasErrors
                              ? { color: theme.palette.error.main }
                              : { color: theme.palette.success.main }
                          }
                        >
                          {hasErrors ? row.errors.join("; ") : "Ready"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : null}

        {lastResult ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Created {lastResult.created}, updated {lastResult.updated}, unchanged{" "}
              {lastResult.unchanged}
              {lastResult.skipped > 0 ? `, skipped blank rows ${lastResult.skipped}` : ""}.
            </Typography>
            {lastResult.errors.length > 0 ? (
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Issue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lastResult.errors.slice(0, 50).map((err, idx) => (
                    <TableRow key={`${err.rowNumber}-${idx}-${err.message}`}>
                      <TableCell>{err.rowNumber}</TableCell>
                      <TableCell>{err.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : null}
          </Box>
        ) : null}
      </CsvImportDialog>
    </>
  );
}
